import { useCallback, useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  HIGHLIGHT_ACCENTS,
  LANDING_PLAN_HIGHLIGHTS,
} from '@/lib/landingHighlights';

export function LandingPlanHighlights() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 8);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons]);

  const scrollByCard = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-highlight-card]');
    const amount = (card?.offsetWidth ?? 300) + 16;
    el.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  return (
    <section className="py-10 md:py-14">
      <div className="container mx-auto px-4 mb-8 text-center">
        <p className="text-sm font-medium text-hospital-primary mb-1">
          Included in every plan
        </p>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
          What you get with AM CARE
        </h3>
      </div>

      <div className="relative">
        <div className="container mx-auto px-4 hidden sm:flex justify-end gap-2 mb-4">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-full bg-white/80 backdrop-blur-sm shadow-sm border-gray-200"
            onClick={() => scrollByCard('left')}
            disabled={!canScrollLeft}
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-full bg-white/80 backdrop-blur-sm shadow-sm border-gray-200"
            onClick={() => scrollByCard('right')}
            disabled={!canScrollRight}
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div
          ref={scrollRef}
          className={cn(
            'flex gap-4 overflow-x-auto pb-3 px-4 md:px-[max(1rem,calc((100vw-1280px)/2+1rem))]',
            'snap-x snap-mandatory scroll-smooth',
            '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          )}
        >
          {LANDING_PLAN_HIGHLIGHTS.map((highlight) => {
            const Icon = highlight.icon;
            const theme = HIGHLIGHT_ACCENTS[highlight.accent];

            return (
              <article
                key={highlight.id}
                data-highlight-card
                className={cn(
                  'group relative shrink-0 snap-center overflow-hidden',
                  'w-[min(calc(100vw-2rem),320px)] sm:w-[300px]',
                  'rounded-2xl border border-white/60',
                  'bg-gradient-to-b shadow-md hover:shadow-lg',
                  'transition-all duration-300 hover:-translate-y-0.5',
                  theme.card
                )}
              >
                <div
                  className={cn(
                    'pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl',
                    theme.glow
                  )}
                />

                <div className="relative flex flex-col gap-4 p-6 min-h-[200px]">
                  <div
                    className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-xl shadow-lg',
                      theme.icon
                    )}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2} />
                  </div>

                  <div className="space-y-2 flex-1">
                    <h4 className="text-lg font-bold text-gray-900 tracking-tight">
                      {highlight.title}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {highlight.summary}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {highlight.chips.map((chip) => (
                      <span
                        key={chip}
                        className={cn(
                          'inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium',
                          theme.chip
                        )}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p className="text-center text-xs text-gray-400 mt-3 sm:hidden">
          Swipe to explore →
        </p>
      </div>
    </section>
  );
}
